import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { ThumbsRating } from '@shared-ui/components';
import type { ThumbsValue } from '@shared-ui/components';
import type { UserFeedback } from '../../types';

interface AdminFeedbackPanelProps {
    submissionId: string;
    userFeedback: UserFeedback | undefined;
}

export function AdminFeedbackPanel({ submissionId, userFeedback }: AdminFeedbackPanelProps) {
    const queryClient = useQueryClient();
    const [comment, setComment] = useState('');

    const mutation = useMutation({
        mutationFn: ({ rating, comment }: { rating: 'positive' | 'negative'; comment?: string }) =>
            adminApi.submitAdminFeedback(submissionId, rating, comment),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
            setComment('');
        },
    });

    return (
        <div className="admin-feedback-section">
            <h4>Assessment Feedback</h4>
            {Array.isArray(userFeedback) && userFeedback.length > 0 && (
                <div className="feedback-entries">
                    {userFeedback.map((entry, i) => (
                        <div key={i} className={`admin-feedback-status ${entry.rating}`}>
                            {entry.rating === 'positive' ? '👍' : '👎'}
                            {' '}<strong>{entry.role}</strong>
                            {entry.submittedAt && ` at ${format(new Date(entry.submittedAt), 'PPp')}`}
                            {entry.comment && ` — "${entry.comment}"`}
                        </div>
                    ))}
                </div>
            )}
            <p className="field-desc">Thumbs up verifies as ground truth; thumbs down flags for review.</p>
            <div className="admin-feedback-row">
                <input
                    type="text"
                    className="feedback-comment-input"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Optional comment..."
                />
                <ThumbsRating
                    initialRating={(userFeedback ?? []).find(f => f.role === 'admin')?.rating as ThumbsValue | undefined ?? null}
                    onRate={(rating) => mutation.mutateAsync({ rating, comment: comment || undefined }).then(() => undefined)}
                    renderPositive={() => '👍 Good'}
                    renderNegative={() => '👎 Bad'}
                    positiveTitle="Verify as ground truth"
                    negativeTitle="Flag for review"
                />
            </div>
            {mutation.isSuccess && (
                <div className="admin-feedback-status positive">
                    Auto-imported to {mutation.data?.autoImportTarget}
                </div>
            )}
        </div>
    );
}
